import { AuthRepository } from '../core/domain/repositories/auth_repository';
import { LoginUseCase } from '../core/domain/usecases/login_usecase';
import { LogoutUseCase } from '../core/domain/usecases/logout_usecase';
import { GetPersistedTokenUseCase } from '../core/domain/usecases/get_persisted_token_usecase';
import { AuthRepositoryImpl } from '../core/data/repositories_impl/auth_repository_impl';
import { CookieAuthDataSource } from '../core/data/datasources/local/cookie_auth_datasource';
import { HttpAuthDataSource } from '../core/data/datasources/remote/auth_remote_datasource';
import { API_CONFIG } from '../config/constants';
import { FeatureDIContainer } from '../features/di';

/**
 * Container de Injeção de Dependências
 * Centraliza a criação e configuração de todas as dependências
 */
export class DIContainer {
  private static instance: DIContainer;
  private dependencies = new Map<string, any>();
  private featureDI!: FeatureDIContainer; // Usar ! para indicar que será inicializado no constructor

  private constructor() {
    this.setupDependencies();
    this.setupFeatures();
  }

  static getInstance(): DIContainer {
    if (!DIContainer.instance) {
      DIContainer.instance = new DIContainer();
    }
    return DIContainer.instance;
  }

  private setupDependencies(): void {
    // Usar configurações centralizadas
    console.log('🔧 DI Container configurado com API_BASE_URL:', API_CONFIG.baseURL);

    // Data Sources - usar cookies para armazenamento local
    const localDataSource = new CookieAuthDataSource();
    const remoteDataSource = new HttpAuthDataSource(API_CONFIG.baseURL, fetch.bind(window));

    // Repositories
    const authRepository = new AuthRepositoryImpl(localDataSource, remoteDataSource);

    // Use Cases
    const loginUseCase = new LoginUseCase(authRepository);
    const logoutUseCase = new LogoutUseCase(authRepository);
    const getPersistedTokenUseCase = new GetPersistedTokenUseCase(authRepository);

    // Registra as dependências
    this.dependencies.set('AuthRepository', authRepository);
    this.dependencies.set('LoginUseCase', loginUseCase);
    this.dependencies.set('LogoutUseCase', logoutUseCase);
    this.dependencies.set('GetPersistedTokenUseCase', getPersistedTokenUseCase);
  }

  private setupFeatures(): void {
    console.log('🏗️ DIContainer: Inicializando features...');
    
    // Inicializar container de features
    this.featureDI = FeatureDIContainer.getInstance();
    
    // Verificar saúde das features
    const healthCheck = this.featureDI.healthCheck();
    if (healthCheck.healthy) {
      console.log('✅ DIContainer: Todas as features inicializadas com sucesso');
    } else {
      console.warn('⚠️ DIContainer: Algumas features com problemas:', healthCheck.features);
    }
  }

  get<T>(key: string): T {
    const dependency = this.dependencies.get(key);
    if (!dependency) {
      throw new Error(`Dependency '${key}' not found`);
    }
    return dependency;
  }

  // ============ CORE DEPENDENCIES ============

  // Métodos de conveniência para tipos específicos
  getAuthRepository(): AuthRepository {
    return this.get<AuthRepository>('AuthRepository');
  }

  getLoginUseCase(): LoginUseCase {
    return this.get<LoginUseCase>('LoginUseCase');
  }

  getLogoutUseCase(): LogoutUseCase {
    return this.get<LogoutUseCase>('LogoutUseCase');
  }

  getGetPersistedTokenUseCase(): GetPersistedTokenUseCase {
    return this.get<GetPersistedTokenUseCase>('GetPersistedTokenUseCase');
  }

  // ============ FEATURE DEPENDENCIES ============

  /**
   * Acesso ao container de features
   */
  get features(): FeatureDIContainer {
    return this.featureDI;
  }

  /**
   * Shortcuts para features específicas
   */
  get dashboard() {
    return this.featureDI.dashboard;
  }

  // get kyozap() {
  //   return this.featureDI.kyozap;
  // }

  // ============ MANAGEMENT METHODS ============

  /**
   * Health check completo do sistema
   */
  healthCheck(): {
    core: { healthy: boolean; dependencies: string[] };
    features: { healthy: boolean; features: Record<string, { healthy: boolean; issues: string[] }> };
    overall: boolean;
  } {
    console.log('🔍 DIContainer: Executando health check completo...');

    const coreDeps = Array.from(this.dependencies.keys());
    const coreHealthy = coreDeps.length > 0;

    const featuresHealth = this.featureDI.healthCheck();

    const overall = coreHealthy && featuresHealth.healthy;

    const result = {
      core: { healthy: coreHealthy, dependencies: coreDeps },
      features: featuresHealth,
      overall
    };

    if (overall) {
      console.log('✅ DIContainer: Health check completo passou');
    } else {
      console.error('❌ DIContainer: Health check completo falhou', result);
    }

    return result;
  }

  /**
   * Debug: Imprime informações detalhadas do container
   */
  debug(): void {
    console.group('📊 DIContainer Debug Info');
    
    console.log('Core Dependencies:', Array.from(this.dependencies.keys()));
    
    const stats = this.featureDI.getStats();
    console.log('Feature Stats:', stats);
    
    const health = this.healthCheck();
    console.log('Health Check:', health);
    
    console.groupEnd();
  }
}
